/**
 * Keeps the bundles in the cart on the discount they are entitled to today.
 *
 * The Cart Transform (extensions/sofa-bundle-rust) merges a bundle's module
 * lines into one line and forwards each module's discount claim on that line's
 * "_dsig" property as comma-joined "<variant id>|<percentage>:<expiry>:<sig>"
 * entries. A claim is a snapshot signed when the bundle was added, and neither
 * Function can read collection metafields, so a discount that was edited,
 * ended or newly created afterwards is invisible to them.
 *
 * This compares what each bundle carries with the claims the live collection
 * data produces today (sections/discount-validator.liquid). On any difference
 * - expired, ended, changed, or newly available - one modal for the whole cart
 * names the bundles and asks the shopper to update. It has a single Update
 * button and can't be closed any other way - until an update fails, after
 * which it can be closed (and stays closed for the session) so a failure never
 * traps the shopper. Updating replaces each bundle: its
 * modules are added again with today's claims, then the old line is removed
 * (a merged line's claims can't be edited in place). Modules with no discount
 * today simply carry no claim, so an ended discount ends up at full price.
 *
 * The modal can open on any page.
 *
 * A line holding several identical bundles is replaced by ONE bundle; the
 * modal says so.
 *
 * Fallback when a bundle can't be rebuilt (no live module data, or module
 * quantities that don't divide evenly across the line): if its carried claims
 * are expired by their own date, the bundle is removed instead - same boundary
 * as the Functions, expired once the expiry date is before today
 * ("YYYY-MM-DD", shop-local).
 *
 * Runs: on page load, whenever the cart drawer opens, and on a recurring
 * interval that pauses while the tab is hidden or the shopper's been idle.
 */
(function () {
	const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
	const IDLE_THRESHOLD_MS = 15 * 60 * 1000; // stop looping after 15 minutes of no activity
	const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

	let lastActivityAt = Date.now();
	let intervalHandle = null;
	let inFlight = false;

	// The modal, created on first use, and the updates it is offering.
	let modal = null;
	let pendingUpdates = [];
	let updating = false;
	let modalStrings = null;
	// Set after an update fails: the modal then becomes dismissible so a failure
	// never traps the shopper, and stays dismissed for this browser session.
	let failed = false;
	let dismissed = false;
	const DISMISS_KEY = 'dv-dismissed';

	function markActivity() {
		lastActivityAt = Date.now();
	}
	['mousemove', 'keydown', 'click', 'touchstart', 'scroll'].forEach((evt) =>
		document.addEventListener(evt, markActivity, { passive: true }),
	);

	// Returns { today, strings, bundlesByKey } or null when the data is unavailable.
	async function fetchLiveData() {
		const url = new URL(window.location.href);
		url.searchParams.set('section_id', 'discount-validator');
		const response = await fetch(url.toString(), { credentials: 'same-origin' });
		if (!response.ok) return null;
		const html = await response.text();
		const doc = new DOMParser().parseFromString(html, 'text/html');
		const script = doc.getElementById('DiscountValidatorData');
		if (!script) return null;
		try {
			const data = JSON.parse(script.textContent);
			if (!ISO_DATE.test(data.today)) return null;
			const bundlesByKey = new Map((data.bundles || []).map((b) => [b.key, b]));
			return { today: data.today, strings: data.strings || {}, bundlesByKey };
		} catch (e) {
			return null; // unreadable live data: change nothing
		}
	}

	async function fetchCurrentCart() {
		const response = await fetch('/cart.js', { credentials: 'same-origin' });
		return response.json();
	}

	// "<variant id>|<claim>,..." -> { variantId: claim }
	function carriedClaims(line) {
		const carried = {};
		const value = line.properties && line.properties._dsig;
		if (!value) return carried;
		String(value)
			.split(',')
			.forEach((entry) => {
				const [variantId, claim] = entry.split('|');
				if (variantId && claim) carried[variantId] = claim;
			});
		return carried;
	}

	function isBundleLine(line) {
		const p = line.properties || {};
		return Boolean(p._bundle_config || p._msku || p._bundle_title || String(p._dsig || '').indexOf('|') !== -1);
	}

	// True when any module's claim differs from what today's data gives it,
	// in either direction ('' = no discount applies).
	function needsReplacing(carried, bundle) {
		return Object.keys(bundle.claims).some((variantId) => (bundle.claims[variantId] || '') !== (carried[variantId] || ''));
	}

	// Judged by the date inside each carried claim. An expiry that isn't a
	// "YYYY-MM-DD" date is a claim from before that format existed (a unix
	// timestamp), which the checkout validator rejects as expired.
	function expiredByOwnDate(live, carried) {
		return Object.keys(carried).some((variantId) => {
			const expiry = carried[variantId].split(':')[1] || '';
			// An empty expiry means no expiry date was set: never expires.
			if (expiry === '') return false;
			return !ISO_DATE.test(expiry) || expiry < live.today;
		});
	}

	// The merged line can only be rebuilt when the section gave every module's
	// variant and quantity. The section reports each module's total across the
	// whole line, so a line holding several identical bundles is one bundle's
	// worth times the line quantity; if a total doesn't divide evenly, the split
	// is unclear and the line isn't rebuilt. Returns each module's quantity for
	// ONE bundle (`quantity`) and for the whole line (`total`).
	function rebuildableModules(line, bundle) {
		if (!bundle || !Array.isArray(bundle.modules) || !bundle.modules.length) return null;
		const bundles = Math.max(1, Number(line.quantity) || 1);
		const modules = [];
		for (const m of bundle.modules) {
			const total = Number(m.quantity);
			if (!(Number(m.id) > 0) || !(total > 0) || total % bundles !== 0) return null;
			modules.push({ id: m.id, quantity: total / bundles, total });
		}
		return modules;
	}

	// The modules of a bundle as cart items, each carrying the given claim (or
	// none), under a fresh bundle id. `field` picks the quantity: 'quantity'
	// for one bundle, 'total' to put back exactly what the line held.
	function bundleItems(line, modules, claimFor, field) {
		const p = line.properties || {};
		const bundleId = line.variant_id + '__' + Date.now().toString(36) + '-' + Math.floor(Math.random() * 1e6).toString(36);
		const shared = { _bundle_id: bundleId };
		['_bundle_config', '_bundle_image', '_msku', '_bundle_title'].forEach((name) => {
			if (p[name]) shared[name] = p[name];
		});
		return modules.map((m) => {
			const properties = Object.assign({}, shared);
			const claim = claimFor(m.id);
			if (claim) properties._dsig = claim;
			return { id: Number(m.id), quantity: Number(m[field]), properties };
		});
	}

	// Resolves to whether the items were added; a network failure counts as
	// "not added" rather than throwing, so callers can always roll back.
	async function addItems(items) {
		try {
			const added = await fetch('/cart/add.js', {
				method: 'POST',
				credentials: 'same-origin',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ items }),
			});
			return added.ok;
		} catch (e) {
			return false;
		}
	}

	async function removeLines(keys) {
		const updates = {};
		keys.forEach((key) => {
			updates[key] = 0;
		});
		const response = await fetch('/cart/update.js', {
			method: 'POST',
			credentials: 'same-origin',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ updates }),
		});
		const cart = await response.json().catch(() => null);
		const remaining = cart && Array.isArray(cart.items) ? cart.items.map((item) => item.key) : null;
		// A 200 doesn't prove the line went away (update.js ignores keys it
		// doesn't recognise), so check the returned cart.
		const stillThere = remaining ? keys.some((key) => remaining.includes(key)) : false;
		if (!response.ok || stillThere) {
			console.warn('[discount-validator] remove failed', { status: response.status, stillThere, keys, remaining, cart });
		}
		return response.ok && !stillThere;
	}

	// An update promises ONE bundle. Identical bundles merge into one line (or
	// sit as siblings), so after the add, cut whatever matches this bundle down
	// to a single unit - this also catches an old copy that survived the
	// removal.
	async function ensureSingleBundle(line) {
		try {
			const cart = await fetchCurrentCart();
			const config = (line.properties || {})._bundle_config;
			const same = (cart.items || []).filter(
				(item) => item.variant_id === line.variant_id && (item.properties || {})._bundle_config === config,
			);
			const updates = {};
			same.forEach((item, index) => {
				if (index > 0) updates[item.key] = 0;
				else if (item.quantity > 1) updates[item.key] = 1;
			});
			if (!Object.keys(updates).length) return;
			await fetch('/cart/update.js', {
				method: 'POST',
				credentials: 'same-origin',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ updates }),
			});
		} catch (e) {
			// Best effort: the update itself already succeeded.
		}
	}

	// A merged line's key comes from its parent variant and merged attributes,
	// not its bundle id, so an old bundle and its replacement can share one key
	// and "remove that key" would be ambiguous. So the old bundle goes first,
	// while its key is unique, and is put back if adding the new one fails.
	async function replaceBundle(line, bundle, modules, carried) {
		// Updating leaves ONE bundle even if the line held several; a failed
		// update puts back everything the line held.
		const freshItems = bundleItems(line, modules, (id) => bundle.claims[id], 'quantity');
		const originalItems = bundleItems(line, modules, (id) => carried[id], 'total');

		let removed = false;
		try {
			removed = await removeLines([line.key]);
		} catch (e) {
			removed = false;
		}
		if (!removed) {
			console.warn('[discount-validator] could not remove bundle line', line.key);
			return false;
		}

		if (await addItems(freshItems)) {
			await ensureSingleBundle(line);
			return true;
		}

		console.warn('[discount-validator] could not add the updated bundle', freshItems);
		if (!(await addItems(originalItems))) {
			// The old bundle is gone and neither version could be added. The cart
			// has changed, so refresh its display.
			refreshCart();
		}
		return false;
	}

	function refreshCart() {
		document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
		if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
			publish(PUB_SUB_EVENTS.cartUpdate, { source: 'discount-validator' });
		}
	}

	/* ---------------------------------------------------------------
	   The update modal
	   --------------------------------------------------------------- */

	function bundleTitle(line) {
		const p = line.properties || {};
		return p._bundle_title || line.product_title || line.title || '';
	}

	function ensureModal() {
		if (modal) return modal;

		// A plain overlay rather than a native <dialog>. Nothing in it closes it:
		// no close button, no Escape handling, and the backdrop ignores clicks.
		const root = document.createElement('div');
		root.className = 'dv-modal';
		root.setAttribute('role', 'dialog');
		root.setAttribute('aria-modal', 'true');
		root.setAttribute('aria-labelledby', 'dv-modal-title');
		root.setAttribute('aria-describedby', 'dv-modal-body');
		root.hidden = true;
		root.innerHTML =
			'<div class="dv-modal__backdrop"></div>' +
			'<div class="dv-modal__box">' +
			'<h2 class="dv-modal__title" id="dv-modal-title"></h2>' +
			'<p class="dv-modal__body" id="dv-modal-body"></p>' +
			'<ul class="dv-modal__bundles"></ul>' +
			'<p class="dv-modal__note" hidden></p>' +
			'<p class="dv-modal__error" role="alert" hidden></p>' +
			'<button type="button" class="button button--primary dv-modal__confirm"></button>' +
			'<button type="button" class="button button--secondary dv-modal__close" hidden>Close</button>' +
			'</div>';
		// The button is the only focusable thing in the modal, so keep focus on it.
		root.addEventListener('keydown', (event) => {
			if (event.key === 'Escape' && failed) {
				dismiss();
			} else if (event.key === 'Tab' && !failed) {
				event.preventDefault();
				modal.confirm.focus();
			}
		});
		root.querySelector('.dv-modal__backdrop').addEventListener('click', () => {
			if (failed) dismiss();
		});
		document.body.appendChild(root);

		modal = {
			root,
			title: root.querySelector('.dv-modal__title'),
			body: root.querySelector('.dv-modal__body'),
			list: root.querySelector('.dv-modal__bundles'),
			note: root.querySelector('.dv-modal__note'),
			error: root.querySelector('.dv-modal__error'),
			confirm: root.querySelector('.dv-modal__confirm'),
			close: root.querySelector('.dv-modal__close'),
		};
		modal.confirm.addEventListener('click', applyUpdates);
		modal.close.addEventListener('click', dismiss);
		return modal;
	}

	function isModalOpen() {
		return Boolean(modal && !modal.root.hidden);
	}

	function showModal() {
		const m = ensureModal();
		if (!m.root.hidden) return;
		m.root.hidden = false;
		document.documentElement.classList.add('dv-modal-open'); // stops the page scrolling behind it
		m.confirm.focus();
	}

	function hideModal() {
		if (!modal) return;
		modal.root.hidden = true;
		document.documentElement.classList.remove('dv-modal-open');
	}

	function staleSignature(items) {
		return items.map((item) => item.line.key).sort().join('|');
	}

	function wasDismissed(items) {
		try {
			return sessionStorage.getItem(DISMISS_KEY) === staleSignature(items);
		} catch (e) {
			return false;
		}
	}

	function dismiss() {
		dismissed = true;
		try {
			sessionStorage.setItem(DISMISS_KEY, staleSignature(pendingUpdates));
		} catch (e) {
			// Storage unavailable: dismissed for this page view only.
		}
		hideModal();
	}

	function promptToUpdate(items, strings) {
		if (updating) return; // mid-update: leave the modal alone
		if (dismissed || wasDismissed(items)) return; // the shopper closed it after a failed update
		const m = ensureModal();
		pendingUpdates = items;
		modalStrings = strings;

		m.title.textContent = strings.title || '';
		m.body.textContent = strings.body || '';
		m.list.replaceChildren(
			...items.map((item) => {
				const li = document.createElement('li');
				li.textContent = bundleTitle(item.line);
				return li;
			}),
		);
		// Updating leaves one bundle, so say so when a line held more than one.
		m.note.textContent = strings.quantityNote || '';
		m.note.hidden = !items.some((item) => Number(item.line.quantity) > 1);
		m.error.hidden = true;
		m.close.hidden = true;
		failed = false;
		m.confirm.disabled = false;
		m.confirm.textContent = strings.confirm || '';
		showModal();
	}

	async function applyUpdates() {
		if (updating || !modal) return;
		updating = true;
		modal.confirm.disabled = true;
		modal.confirm.textContent = (modalStrings && modalStrings.working) || '';
		modal.error.hidden = true;

		// Look at the cart again right now: the lines offered when the modal
		// opened may have changed keys since, and removing a stale key silently
		// does nothing - which would leave the old bundle next to the new one.
		try {
			const fresh = await collectStale();
			if (fresh) pendingUpdates = fresh.stale;
		} catch (e) {
			// Fall back to the list the modal was opened with.
		}

		let changed = false;
		let failed = false;
		while (pendingUpdates.length) {
			const item = pendingUpdates[0];
			if (await replaceBundle(item.line, item.bundle, item.modules, item.carried)) {
				pendingUpdates.shift(); // done; a retry only redoes what's left
				changed = true;
			} else {
				failed = true;
				break;
			}
		}

		updating = false;

		if (failed) {
			if (changed) refreshCart();
			failed = true;
			modal.close.hidden = false;
			modal.error.textContent = (modalStrings && modalStrings.error) || '';
			modal.error.hidden = false;
			modal.confirm.disabled = false;
			modal.confirm.textContent = (modalStrings && modalStrings.confirm) || '';
			return;
		}

		// Every drawer, header count and total on the page needs the new cart, so
		// start from a clean page. The button stays disabled until it lands.
		window.location.reload();
	}

	/* ---------------------------------------------------------------
	   The check
	   --------------------------------------------------------------- */

	// Reads the cart and the live data and sorts the bundles into those to
	// offer an update for (`stale`) and those to remove outright (`removals`).
	// Resolves to null when there is nothing to judge or the live data is
	// unavailable - never guess.
	async function collectStale() {
		const cart = await fetchCurrentCart();
		if (!cart.items || !cart.items.length) return null;

		// Skip the live-data fetch entirely when nothing in the cart is a bundle.
		const candidates = cart.items.filter(isBundleLine);
		if (!candidates.length) return null;

		const live = await fetchLiveData();
		if (!live) return null;

		const stale = [];
		const removals = [];

		for (const line of candidates) {
			const carried = carriedClaims(line);
			const bundle = live.bundlesByKey.get(line.key);
			const modules = rebuildableModules(line, bundle);

			if (bundle && modules && needsReplacing(carried, bundle)) {
				stale.push({ line, bundle, modules, carried });
			} else if (expiredByOwnDate(live, carried)) {
				// Can't be rebuilt, and its own claims say it has expired.
				removals.push(line.key);
			}
		}

		return { live, stale, removals };
	}

	async function runValidation() {
		if (inFlight) return;
		inFlight = true;
		try {
			const result = await collectStale();
			if (!result) {
				if (isModalOpen() && !updating) hideModal(); // nothing left to update
				return;
			}
			const { live, stale, removals } = result;

			if (removals.length) {
				// Refresh even if the request rejects or reports failure: some of the
				// lines may already be gone.
				try {
					await removeLines(removals);
				} finally {
					refreshCart();
				}
			}

			if (stale.length) {
				promptToUpdate(stale, live.strings);
			} else if (!stale.length && isModalOpen() && !updating) {
				hideModal(); // whatever was stale has been fixed elsewhere
			}
		} catch (e) {
			// A failed run leaves the cart as it was; the next trigger tries again.
		} finally {
			inFlight = false;
		}
	}

	function scheduleLoop() {
		if (intervalHandle) return;
		intervalHandle = setInterval(() => {
			if (document.hidden) return; // paused while the tab isn't visible
			if (Date.now() - lastActivityAt > IDLE_THRESHOLD_MS) return; // paused while idle
			runValidation();
		}, CHECK_INTERVAL_MS);
	}

	// 1. Page load / reload
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', runValidation, { once: true });
	} else {
		runValidation();
	}

	// 2. Cart drawer open - a MutationObserver on its class list works
	// regardless of what triggered the open (click, keyboard, or JS calling
	// .open() after an add-to-cart), without needing to modify cart-drawer.js.
	const drawer = document.querySelector('cart-drawer');
	if (drawer) {
		let wasActive = drawer.classList.contains('active');
		new MutationObserver(() => {
			const isActive = drawer.classList.contains('active');
			if (isActive && !wasActive) runValidation();
			wasActive = isActive;
		}).observe(drawer, { attributes: true, attributeFilter: ['class'] });
	}

	// 3. Recurring loop, resumed immediately when the tab becomes visible again.
	document.addEventListener('visibilitychange', () => {
		if (!document.hidden) runValidation();
	});
	scheduleLoop();
})();
